import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

export default function Financial() {
  const [summary, setSummary] = useState(null);
  const [refunds, setRefunds] = useState([]);
  useEffect(() => {
    api.get("/admin/finance/summary").then((r) => setSummary(r.data));
    api.get("/admin/finance/refunds").then((r) => setRefunds(r.data));
  }, []);
  if (!summary) return <div className="p-10 text-slate-500">Loading…</div>;

  const pieData = [
    { name: "Platform", value: summary.platform_fees, color: "#f59e0b" },
    { name: "Partner", value: summary.partner_fees, color: "#38bdf8" },
    { name: "Driver", value: summary.driver_earnings, color: "#94a3b8" },
  ];

  return (
    <div className="p-10 max-w-[1400px]">
      <PageHeader eyebrow="Reports · Money" title="Financial" description="Revenue and fee splits across the DiTours platform." />

      <div className="grid grid-cols-4 gap-5 mb-8">
        <Kpi l="Total revenue" v={`€${summary.total_revenue.toFixed(2)}`} accent />
        <Kpi l="Platform fees" v={`€${summary.platform_fees.toFixed(2)}`} />
        <Kpi l="Partner fees" v={`€${summary.partner_fees.toFixed(2)}`} />
        <Kpi l="Driver earnings" v={`€${summary.driver_earnings.toFixed(2)}`} />
      </div>

      <div className="grid grid-cols-2 gap-5 mb-8">
        <div className="border border-slate-200 bg-white p-6">
          <p className="label-caps mb-4">Fee split</p>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value">
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #f59e0b", color: "#0a0f1e" }} formatter={(v) => `€${v.toFixed(2)}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {pieData.map((p) => (
              <div key={p.name} className="text-center">
                <div className="w-2 h-2 mx-auto mb-1" style={{ background: p.color }} />
                <div className="text-xs text-slate-500">{p.name}</div>
                <div className="text-sm font-mono text-[#0a0f1e]">€{p.value.toFixed(0)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-slate-200 bg-white p-6">
          <p className="label-caps mb-4">Refunds ({refunds.length})</p>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {refunds.length === 0 && <div className="text-xs text-slate-500">No refund requests.</div>}
            {refunds.map((r) => (
              <div key={r.id} className="flex justify-between border-b border-slate-200 pb-2">
                <div>
                  <div className="text-xs text-slate-500 font-mono">{r.booking_id.slice(0, 8)}</div>
                  <div className="text-xs text-slate-500">Penalty: €{r.penalty_amount.toFixed(2)}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-[#0a0f1e]">€{r.refund_amount.toFixed(2)}</div>
                  <div className="text-xs text-amber-400 uppercase">{r.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ l, v, accent }) {
  return (
    <div className={`border p-6 ${accent ? "border-[#f59e0b] bg-[#f59e0b]/5" : "border-slate-200 bg-white"}`}>
      <div className="label-caps mb-3">{l}</div>
      <div className={`font-display text-3xl ${accent ? "text-[#f59e0b]" : "text-[#0a0f1e]"}`}>{v}</div>
    </div>
  );
}
